import {FastDeliverySVG} from 'assets/svg/FastDeliverySVG';
import {ProductRealImages} from 'components/DetailGoodPage/ProductRealImages';
import React, {FC, useEffect, useMemo, useState} from 'react';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Image from 'next/image';

import { getFullProductProps, getProductInitialOptions } from 'entities/products';
import {MAX_SUPPLY_DAYS} from 'shared/lib/constants';
import {pluralizeNoun} from 'shared/lib/pluralizeNoun';
import { useFavorite } from '../../../shared/lib/hooks/useFavorite';
import { useCart } from '../../../shared/lib/hooks/useCart';
import { useDispatch } from 'shared/lib/hooks/useDispatch';
import { closeOneClickModal, setOneClickGood } from 'src/store/goods';
import { metric } from 'features/metric';
import { IProductError, checkProductToBuy } from 'entities/products/lib/checkProductToBuy';
import type { Variant } from '@nebo-team-dev/vobaza.ui.inputs.input-select';
import type { IGood, IOneClickGood, IProductVariant, IVariantProduct } from 'entities/products';
import type { IProfile } from 'app/profile/Data';
import type { IError } from 'src/models/IError';
import type { ISticker } from 'src/models/ISticker';

import { Icon } from '@nebo-team-dev/vobaza.ui.icon/dist';
import { Button } from '@nebo-team-dev/vobaza.ui.button/dist';
import { ProductBadges, ProductPrice, ProductSeller } from 'app/products';
import { Breadcrumbs, BreadcrumbType } from 'app/shared';
import { ProductCompare } from 'components/DetailGoodPage/ProductCompare';
import { Counter } from 'widgets/Counter';
import { ProductsList, CartModal, OneClickModal, ProductOptions } from 'widgets/products';
import { SelectVariationOption } from 'features/product-variation';
import { ProductUSP } from 'components/DetailGoodPage/ProductUSP';
import ProductImages from 'components/DetailGoodPage/ProductImages';
import CartItemChangeModal from 'components/Cart/Modal/CartItemChangeModal';
import { MobileOptions } from 'components/DetailGoodPage/MobileOptions';
import { ProductDescriptions } from 'components/DetailGoodPage/ProductDescription';
import { CardSupplyDays } from 'entities/products/ui/Card/CardSupplyDays';
import { CardExpressSupplyDays } from 'entities/products/ui/Card/CardSupplyDays/Express';

import { ExpressSVG } from 'assets/svg/ExpressSVG';
import PlaceholderImageFull from 'assets/images/placeholder.png';

import styles from './styles.module.scss';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';

const renderEmptyPlaceholder = () => {
  return (
    <div className={styles.capImage}>
      <Image src={PlaceholderImageFull} alt="PlaceholderImage" />
    </div>
  );
};

interface DetailGoodPage {
  initialProduct: IGood;
  options: IProductVariant<{ product: IVariantProduct; param: Variant }>[];
  breadcrumbs: BreadcrumbType[];
  user: IProfile | null;
  stickers: ISticker[];
}

const DetailGoodPage: FC<DetailGoodPage> = ({ initialProduct, options, breadcrumbs, user, stickers = [] }) => {
  const { currentFavorite, toggleFavorite } = useFavorite(initialProduct);
  const [product, setProduct] = useState(initialProduct);
  const [errorToBuy, setErrorToBuy] = useState<IProductError | null>(null);
  const basketItems = useSelector((store: RootState) => store.goods.basketItems);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, (string | number)[]>>(
    getProductInitialOptions(product.variants, product.id)
  );
  const [quantity, setQuantity] = useState(0);

  const router = useRouter();
  const dispatch = useDispatch();
  const supplyDays = Number(initialProduct?.expected_supply_days);

  const { addToCart, isLoading, deleteItem } = useCart({
    ...initialProduct,
    parent_categories: [initialProduct.main_category, ...initialProduct.other_categories],
  });

  const addToCartHandler = async () => {
    try {
      const res = await addToCart({ withModal: false });
      metric.reachGoal('basket');
      setQuantity((oldQuantity) => oldQuantity + 1);
      if (res?.available === false) setProduct((prev) => ({ ...prev, inStonk: false }));
      if (res?.error) setErrorToBuy(res?.error);
    } catch (e) {
      console.error(e);
    }
  };

  const onOneClickOrderError = (_, errors: IError[], goodError?: IProductError | null) => {
    if (goodError) {
      dispatch(closeOneClickModal());
      setErrorToBuy(goodError);
    }
  };

  const handelSelectOption: SelectVariationOption = (id, variantProduct) => {
    const options = { ...selectedOptions };
    options[id] = variantProduct.attributes.find((attr) => attr.id == id)?.value;
    // Определение товара, соответствующего выбранным опциям вариации
    const { slug } = variantProduct;
    router.push(`/product/${slug}`, undefined, { scroll: false });
    setSelectedOptions(options);
  };

  const openOneClickModal = async () => {
    const errorToBuy = await checkProductToBuy(product.id);
    setErrorToBuy(errorToBuy);

    if (errorToBuy) return;

    const parent_categories = breadcrumbs
      .filter((item) => item.title !== 'Каталог мебели')
      .map((it, i) => {
        return { id: i, name: it.title };
      });

    const newGood: IOneClickGood = {
      id: product.id,
      name: product.name,
      price: product.price,
      brand: product.brand,
      quantity: 1,
      images: product.images,
      main_image: product.main_image,
      sku: product.sku,
      list_price: product.list_price,
      parent_categories,
    };

    dispatch(setOneClickGood(newGood));
    metric.reachGoal('buy_with_one_click_form_opened');
  };

  const renderDeliveryInfo = useMemo(() => {
    if (initialProduct?.express) {
       return (
           <div className={styles.expressBlock}>
             <div className={styles.deliveryContent}>
               <ExpressSVG/>
               <span className={styles.deliveryText}>Экспресс</span>
             </div>
             Доставим <CardExpressSupplyDays good={initialProduct}/>
           </div>
       )
    } else if (supplyDays < MAX_SUPPLY_DAYS) {
       return (
           <div className={styles.fastDelivery}>
             <div className={styles.deliveryContent}>
               <FastDeliverySVG/>
               <span
                   className={styles.deliveryText}>Доставим за {supplyDays} {pluralizeNoun(supplyDays, ['день', 'дня', 'дней'])}</span>
             </div>
             <CardSupplyDays className={styles.productSupplyDays} good={initialProduct}/>
           </div>
       )
    }

    return (
        <CardSupplyDays className={styles.productSupplyDays} good={initialProduct}/>
    )
  }, [initialProduct, supplyDays])

  const handleClickFavorite = () => {
    toggleFavorite();
    metric.reachGoal('wishlist');
  };

  const handleCloseError = () => {
    setProduct({ ...product, inStonk: false });
  };

  const decreaseHandler = async () => {
    try {
      const res = await deleteItem();
      !res?.error && setQuantity((oldQuantity) => oldQuantity - 1);
      res?.error && setErrorToBuy(res?.error);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    metric.detail(initialProduct);
  }, []);

  useEffect(() => {
    setProduct(() => initialProduct);
  }, [initialProduct.id]);

  useEffect(() => {
    setSelectedOptions(getProductInitialOptions(product.variants, product.id));
  }, [product.variants]);
  useEffect(() => {
    const goodInCartQuantity = basketItems.find((item) => item.id === initialProduct.id)?.quantity;
    setQuantity(goodInCartQuantity || 0);
  }, [basketItems]);
  return (
    <>
      <Head>
        <meta name="keywords" content={product.seo.keywords} />
        {product.seo.title && <title>{product.seo.title}</title>}
      </Head>
      <div className={styles.page}>
        {errorToBuy && <CartModal onCloseErrorHandle={handleCloseError} />}
        <OneClickModal onError={onOneClickOrderError} user={user} />
        <CartModal />
        {errorToBuy && (
          <CartItemChangeModal
            title={errorToBuy.title || ''}
            description={errorToBuy.description || ''}
            onClose={() => {
              setErrorToBuy(null);
              handleCloseError();
            }}
          />
        )}

        <Breadcrumbs breadcrumbs={breadcrumbs} />

        <div className={`${styles.cardContainer} ${styles.cardContainerInfoBlock}`}>
          <div className={`${styles.productHeadInfo} ${styles.productHeadInfo__mobile}`}>
            {product.subinfo && <>{product.subinfo} • </>}
            <ProductBadges
              className={styles.mobileHidden}
              badges={product.labels}
              stickersIds={product.sticker_ids}
              stickers={stickers}
            />
            <div className={styles.articul}>
              <span>Артикул&nbsp;{product.sku}</span>
            </div>
          </div>
          <h1 className={`${styles.productTitle} ${styles.productTitle__mobile}`}>
            {product.seo.page_name || product.name}
          </h1>
          <div className={styles.productInfo}>
            <div className={`${styles.left} `}>
              <div className={styles.leftContent}>
                <div className={styles.mobileImagesContainer}>
                  {!!product.images?.length ? <ProductImages images={product.images} /> : renderEmptyPlaceholder()}
                  <button
                    className={`${styles.mobileFavoriteButton} ${currentFavorite ? styles.active : ''}`}
                    onClick={currentFavorite ? toggleFavorite : handleClickFavorite}
                  >
                    <Icon name="Favorite" />
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.right}>
              <div className={`${styles.productHeadInfo} ${styles.productHeadInfo__descTop}`}>
                {product.subinfo && <>{product.subinfo} • </>}
                <ProductBadges
                  className={styles.mobileHidden}
                  badges={product.labels}
                  stickersIds={product.sticker_ids}
                  stickers={stickers}
                />
                <div className={styles.articul}>
                  <span>Артикул&nbsp;{product.sku}</span>
                </div>
              </div>
              <div className={`${styles.productCardHeader} ${styles.mobileHidden}`}>
                <h1 className={`${styles.productTitle} ${styles.productTitle__descTop}`}>
                  {product.seo.page_name || product.name}
                </h1>
                <div className={styles.productSubinfo}>
                  <div className={styles.leftMenuActions}>
                    <button
                      className={`${styles.leftMenuActionBtn} ${currentFavorite ? styles.active : ''}`}
                      onClick={currentFavorite ? toggleFavorite : handleClickFavorite}
                    >
                      <Icon name="Favorite" /> <span>{currentFavorite ? 'В избранном' : 'В избранное'}</span>
                    </button>
                    <ProductCompare id={product.id} />
                  </div>
                </div>
              </div>
              <div className={styles.productInfoBlock}>
                <ProductPrice
                  className={styles.productInfoShort}
                  price={product.price}
                  list_price={product.list_price}
                  beforeDiscountPrice={product.list_price}
                  discount={product.percent_off}
                />
              </div>
              {renderDeliveryInfo}
              <div className={` ${styles.cardContainerInfoBlock}`}>
                {options.length > 0 && <span className={styles.chooseOptionLabel}>Выберите параметры</span>}
                {selectedOptions && (
                  <div className={styles.productOptionList}>
                    <ProductOptions
                      currentProductId={product.id}
                      options={options}
                      variants={product.variants}
                      selectedOptions={selectedOptions}
                      handelSelectOption={handelSelectOption}
                    />
                  </div>
                )}
                <ProductUSP initialProduct={initialProduct} />
                {Boolean(product.real_images?.length) && <div className={styles.realImagesMobile}>
                  <ProductRealImages images={product.real_images}/>
                </div>}
                <>
                  <div className={styles.cartButtonsBlock}>
                    <div className={styles.counterBlock}>
                      {Boolean(quantity) && (
                        <Counter
                          value={quantity}
                          decreaseHandler={decreaseHandler}
                          increaseHandler={addToCartHandler}
                          isLoading={isLoading}
                          variant="secondary"
                        />
                      )}
                      <Button
                        size="big"
                        className={styles.cartButton}
                        onClick={
                          Boolean(quantity)
                            ? () => {
                                router.push('/cart');
                              }
                            : addToCartHandler
                        }
                        disabled={isLoading && !product.price}
                        text={Boolean(quantity) ? 'Перейти в корзину' : 'Добавить в корзину'}
                      />
                    </div>
                    {!!product.price && (
                      <Button
                        text="Купить в 1 клик"
                        type="button"
                        disabled={isLoading}
                        onClick={openOneClickModal}
                        className={styles.oneClickButton}
                      />
                    )}
                  </div>

                  <div className={styles.mobileOptionsBlock}>
                    <MobileOptions product={initialProduct} />
                  </div>
                  <ProductSeller merchant={product.merchant} />
                </>
              </div>
              <div className={` ${styles.cardContainerInfoBlock}`}>
                <div className={styles.optionsBlock}>
                  <ProductDescriptions product={initialProduct} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={`${styles.cardContainer} ${styles.cardContainerInfoBlock}`}>
          {product.set && product.set.length > 0 && (
            <div className={styles.productBlockList}>
              <h2 className={styles.productBlockTitle}>Товары из этой серии </h2>
              <ProductsList goods={product.set} stickers={stickers} />
            </div>
          )}
          <div className="container--for-cards">
            {product.similar_products && product.similar_products.length > 0 && (
              <div className={styles.productBlockList}>
                <h2 className={styles.productBlockTitle}>Похожие товары </h2>
                <ProductsList goods={product.similar_products} stickers={stickers} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<DetailGoodPage> = getFullProductProps;

export default DetailGoodPage;
